import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Building2, Search, SlidersHorizontal, Download, RefreshCw, X,
  Bed, Bath, Maximize2, MapPin, DollarSign, Calendar, ChevronLeft,
  TrendingDown, TrendingUp, ArrowUpDown, Clock, Home, Filter, Eye,
  Sparkles, Shield, AlertTriangle, Zap, BarChart3, Send, Loader2,
  ExternalLink, Trophy, Target,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  sold: 'bg-red-500/10 text-red-400 border-red-500/20',
  withdrawn: 'bg-secondary text-muted-foreground border-border',
  expired: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const TYPE_LABELS = {
  single_family: 'Single Family',
  condo: 'Condo',
  townhouse: 'Townhouse',
  multi_family: 'Multi Family',
  land: 'Land',
  commercial: 'Commercial',
  other: 'Other',
};

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Newest First' },
  { value: 'list_price_asc', label: 'Price: Low to High' },
  { value: 'list_price_desc', label: 'Price: High to Low' },
  { value: 'deal_score', label: 'Deal Score: Best First' },
];

export default function PropertiesPage() {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [matchStats, setMatchStats] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [dealScores, setDealScores] = useState({});

  // Filters
  const [filters, setFilters] = useState({
    city: '', status: 'active', property_type: '', min_price: '', max_price: '',
    beds_min: '', baths_min: '', sort_by: 'created_at',
  });
  const [offset, setOffset] = useState(0);
  const limit = 12;

  const headers = { Authorization: `Bearer ${token}` };

  const fetchProperties = useCallback(async (currentOffset = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', limit);
      params.set('offset', currentOffset);
      if (filters.city) params.set('city', filters.city);
      if (filters.status) params.set('status', filters.status);
      if (filters.property_type) params.set('property_type', filters.property_type);
      if (filters.min_price) params.set('min_price', filters.min_price);
      if (filters.max_price) params.set('max_price', filters.max_price);
      if (filters.beds_min) params.set('beds_min', filters.beds_min);
      if (filters.baths_min) params.set('baths_min', filters.baths_min);
      if (filters.sort_by) params.set('sort_by', filters.sort_by);

      const { data } = await axios.get(`${API}/properties/search?${params}`, { headers });
      setProperties(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Properties fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/properties/stats`, { headers });
      setStats(data);
    } catch { /* ignore */ }
  }, [token]);

  const fetchMatchData = useCallback(async () => {
    try {
      const [statsRes, matchesRes] = await Promise.all([
        axios.get(`${API}/property-matches/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/property-matches?limit=5`, { headers }).catch(() => ({ data: { results: [] } })),
      ]);
      setMatchStats(statsRes.data);
      setRecentMatches(matchesRes.data?.results || []);
    } catch { /* ignore */ }
  }, [token]);

  const fetchDealScores = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/deal-scores?limit=200&sort_by=score_desc`, { headers });
      const map = {};
      (data.results || []).forEach(ds => { map[ds.property_id] = ds; });
      setDealScores(map);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    fetchProperties(offset);
    fetchStats();
    fetchMatchData();
    fetchDealScores();
  }, [offset]);

  const handleSearch = () => {
    setOffset(0);
    fetchProperties(0);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/properties/import/run`, { source: 'mock', count: 25 }, { headers });
      toast.success(`Import complete: ${data.new} new, ${data.updated} updated, ${data.price_changes} price changes`);
      fetchProperties(0);
      fetchStats();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Import failed';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const handleViewDetail = async (propId) => {
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/properties/${propId}`, { headers });
      setSelectedProperty(data);
    } catch {
      toast.error('Failed to load property detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ city: '', status: 'active', property_type: '', min_price: '', max_price: '', beds_min: '', baths_min: '', sort_by: 'created_at' });
    setOffset(0);
    setTimeout(() => fetchProperties(0), 0);
  };

  // Detail view
  if (selectedProperty) {
    return <PropertyDetail property={selectedProperty} onBack={() => setSelectedProperty(null)} />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <Building2 size={22} className="text-teal-400" /> Properties
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {total} properties found {stats ? `of ${stats.total} total` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="border-border text-foreground hover:bg-muted gap-1.5"
            data-testid="toggle-filters-btn"
          >
            <Filter size={14} /> Filters
          </Button>
          <Button
            size="sm"
            onClick={handleImport}
            disabled={importing}
            className="bg-teal-600 hover:bg-teal-500 text-foreground gap-1.5"
            data-testid="import-btn"
          >
            {importing ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
            {importing ? 'Importing...' : 'Import Listings'}
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="property-stats">
          <MiniStat label="Total" value={stats.total} icon={<Building2 size={16} />} color="text-teal-400" bg="bg-teal-500/10" />
          <MiniStat label="Active" value={stats.active} icon={<Home size={16} />} color="text-emerald-400" bg="bg-emerald-500/10" />
          <MiniStat label="Avg Price" value={`$${(stats.avg_price || 0).toLocaleString()}`} icon={<DollarSign size={16} />} color="text-blue-400" bg="bg-blue-500/10" />
          <MiniStat label="Avg Sqft" value={(stats.avg_sqft || 0).toLocaleString()} icon={<Maximize2 size={16} />} color="text-violet-400" bg="bg-violet-500/10" />
        </div>
      )}

      {/* Recent Matches */}
      {recentMatches.length > 0 && (
        <Card className="border-border bg-card" data-testid="recent-matches-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-sm flex items-center gap-2">
                <Eye size={14} className="text-emerald-400" /> Recent Matches
                {matchStats?.unread > 0 && (
                  <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-1.5 py-0.5 rounded-full">{matchStats.unread} new</span>
                )}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1.5">
              {recentMatches.map(m => {
                const snap = m.property_snapshot || {};
                const typeStyle = m.match_type === 'price_drop' ? 'text-blue-400 bg-blue-500/10' :
                  m.match_type === 'back_on_market' ? 'text-violet-400 bg-violet-500/10' :
                  m.match_type === 'status_change' ? 'text-amber-400 bg-amber-500/10' :
                  'text-emerald-400 bg-emerald-500/10';
                return (
                  <div key={m.id} className="flex items-center gap-2 p-2 bg-muted rounded-lg hover:bg-muted cursor-pointer transition-colors"
                    onClick={() => handleViewDetail(m.property_id)} data-testid={`recent-match-${m.id}`}>
                    <div className={`h-6 w-6 rounded flex items-center justify-center shrink-0 ${typeStyle.split(' ')[1]}`}>
                      {m.match_type === 'price_drop' ? <TrendingDown size={12} className={typeStyle.split(' ')[0]} /> :
                       <Building2 size={12} className={typeStyle.split(' ')[0]} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground truncate">{snap.address_line}, {snap.city}</p>
                      <p className="text-[10px] text-muted-foreground">{m.match_type.replace(/_/g, ' ')} &middot; ${snap.list_price?.toLocaleString()} &middot; {snap.beds}bd</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(m.created_at)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {showFilters && (
        <Card className="border-border bg-card" data-testid="filters-panel">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Input placeholder="City" value={filters.city} onChange={e => setFilters(f => ({...f, city: e.target.value}))}
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground" data-testid="filter-city" />
              <select value={filters.status} onChange={e => setFilters(f => ({...f, status: e.target.value}))}
                className="h-10 rounded-md bg-muted border border-border text-foreground px-3 text-sm" data-testid="filter-status">
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="sold">Sold</option>
              </select>
              <select value={filters.property_type} onChange={e => setFilters(f => ({...f, property_type: e.target.value}))}
                className="h-10 rounded-md bg-muted border border-border text-foreground px-3 text-sm" data-testid="filter-type">
                <option value="">All Types</option>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select value={filters.sort_by} onChange={e => setFilters(f => ({...f, sort_by: e.target.value}))}
                className="h-10 rounded-md bg-muted border border-border text-foreground px-3 text-sm" data-testid="filter-sort">
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <Input placeholder="Min Price" type="number" value={filters.min_price}
                onChange={e => setFilters(f => ({...f, min_price: e.target.value}))}
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground" data-testid="filter-min-price" />
              <Input placeholder="Max Price" type="number" value={filters.max_price}
                onChange={e => setFilters(f => ({...f, max_price: e.target.value}))}
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground" data-testid="filter-max-price" />
              <Input placeholder="Min Beds" type="number" value={filters.beds_min}
                onChange={e => setFilters(f => ({...f, beds_min: e.target.value}))}
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground" data-testid="filter-beds" />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSearch} className="bg-teal-600 hover:bg-teal-500 text-foreground flex-1 gap-1" data-testid="apply-filters-btn">
                  <Search size={14} /> Search
                </Button>
                <Button size="sm" variant="ghost" onClick={clearFilters} className="text-muted-foreground hover:text-foreground" data-testid="clear-filters-btn">
                  <X size={14} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Property Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
        </div>
      ) : properties.length === 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="py-16 text-center">
            <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-2">No properties found</p>
            <p className="text-xs text-muted-foreground mb-4">Try importing listings or adjusting your filters</p>
            <Button size="sm" onClick={handleImport} disabled={importing} className="bg-teal-600 hover:bg-teal-500 text-foreground gap-1.5" data-testid="empty-import-btn">
              <Download size={14} /> Import Listings
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="properties-grid">
            {properties.map(prop => (
              <PropertyCard key={prop.id} property={prop} onView={() => handleViewDetail(prop.id)} dealScore={dealScores[prop.id]} />
            ))}
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between pt-2" data-testid="pagination">
              <p className="text-xs text-muted-foreground">
                Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  className="border-border text-foreground hover:bg-muted" data-testid="prev-page-btn">
                  Previous
                </Button>
                <Button size="sm" variant="outline" disabled={offset + limit >= total}
                  onClick={() => setOffset(offset + limit)}
                  className="border-border text-foreground hover:bg-muted" data-testid="next-page-btn">
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function PropertyCard({ property, onView, dealScore }) {
  const p = property;
  const statusStyle = STATUS_STYLES[p.status] || STATUS_STYLES.active;
  const typeLabel = TYPE_LABELS[p.property_type] || p.property_type;
  const priceChanged = p.original_list_price && p.original_list_price !== p.list_price;

  const scoreColor = dealScore
    ? dealScore.opportunity_level === 'exceptional' ? 'bg-amber-500 text-black'
    : dealScore.opportunity_level === 'strong' ? 'bg-emerald-500 text-black'
    : dealScore.opportunity_level === 'moderate' ? 'bg-blue-500 text-white'
    : 'bg-muted-foreground text-foreground'
    : null;

  return (
    <Card className="border-border bg-card hover:border-border transition-all group cursor-pointer overflow-hidden"
      onClick={onView} data-testid={`property-card-${p.id}`}>
      {/* Photo */}
      <div className="relative h-40 bg-muted overflow-hidden">
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.address_line} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Building2 size={32} className="text-muted-foreground" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          <Badge className={`text-[10px] ${statusStyle}`}>{p.status}</Badge>
        </div>
        {dealScore && (
          <div className="absolute top-2 right-2" data-testid={`deal-score-badge-${p.id}`}>
            <div className={`flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${scoreColor}`}>
              <Trophy size={10} /> {dealScore.score}
            </div>
          </div>
        )}
        <div className="absolute bottom-2 right-2">
          <span className="bg-black/70 backdrop-blur-sm text-foreground text-xs font-semibold px-2 py-1 rounded">
            ${p.list_price?.toLocaleString()}
          </span>
        </div>
      </div>

      <CardContent className="p-3 space-y-2">
        {/* Address */}
        <div>
          <p className="text-sm font-medium text-foreground truncate" data-testid="property-address">{p.address_line}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin size={10} /> {p.city}, {p.state} {p.postal_code}
          </p>
        </div>

        {/* Specs Row */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Bed size={12} /> {p.beds} bd</span>
          <span className="flex items-center gap-1"><Bath size={12} /> {p.baths} ba</span>
          <span className="flex items-center gap-1"><Maximize2 size={12} /> {p.sqft?.toLocaleString()} sqft</span>
        </div>

        {/* Bottom row */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-[10px] text-muted-foreground">{typeLabel}</span>
          <div className="flex items-center gap-2">
            {priceChanged && (
              <span className="text-[10px] flex items-center gap-0.5">
                {p.list_price < p.original_list_price
                  ? <><TrendingDown size={10} className="text-emerald-400" /><span className="text-emerald-400 line-through">${p.original_list_price?.toLocaleString()}</span></>
                  : <><TrendingUp size={10} className="text-red-400" /><span className="text-red-400 line-through">${p.original_list_price?.toLocaleString()}</span></>
                }
              </span>
            )}
            {p.days_on_market > 0 && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <Clock size={10} /> {p.days_on_market}d
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function PropertyDetail({ property, onBack }) {
  const { token } = useAuth();
  const p = property;
  const statusStyle = STATUS_STYLES[p.status] || STATUS_STYLES.active;
  const typeLabel = TYPE_LABELS[p.property_type] || p.property_type;
  const priceHistory = p.price_history || [];
  const events = p.events || [];
  const [analysis, setAnalysis] = useState(p.analysis || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [cma, setCma] = useState(null);
  const [generatingCma, setGeneratingCma] = useState(false);
  const [dealScore, setDealScore] = useState(p.deal_score || null);
  const [scoring, setScoring] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const runAnalysis = async (useAi = true) => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/property-analysis/${p.id}/run`,
        { use_ai: useAi, force: true }, { headers });
      setAnalysis(data);
      toast.success(`Analysis complete (${data.analysis_type})`);
    } catch {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const generateCma = async () => {
    setGeneratingCma(true);
    try {
      const { data } = await axios.post(`${API}/cma/generate`,
        { property_id: p.id, use_ai: true, comp_count: 5 }, { headers });
      setCma(data);
      toast.success(`CMA generated with ${data.comparables?.length || 0} comparables`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CMA generation failed');
    } finally {
      setGeneratingCma(false);
    }
  };

  const shareCma = async () => {
    if (!cma) return;
    try {
      const { data } = await axios.post(`${API}/cma/report/${cma.id}/share`, {}, { headers });
      setCma(data);
      const shareUrl = `${window.location.origin}/api/cma/shared/${data.share_token}/html`;
      navigator.clipboard?.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
    } catch {
      toast.error('Failed to share CMA');
    }
  };

  const runScoring = async () => {
    setScoring(true);
    try {
      const { data } = await axios.post(`${API}/deal-scores/${p.id}/run`, {}, { headers });
      setDealScore(data);
      toast.success(`Deal score: ${data.score}/100 (${data.opportunity_level})`);
    } catch {
      toast.error('Scoring failed');
    } finally {
      setScoring(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-5" data-testid="property-detail">
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-list-btn">
        <ChevronLeft size={16} /> Back to Properties
      </button>

      {/* Hero */}
      <div className="relative rounded-xl overflow-hidden h-48 sm:h-64 bg-muted">
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.address_line} className="w-full h-full object-cover" />
        ) : (
          <div className="flex items-center justify-center h-full"><Building2 size={48} className="text-muted-foreground" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={`text-xs ${statusStyle}`}>{p.status}</Badge>
            <Badge className="text-xs bg-accent/50 text-foreground border-border">{typeLabel}</Badge>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">{p.address_line}</h1>
          <p className="text-sm text-foreground">{p.city}, {p.state} {p.postal_code}</p>
        </div>
      </div>

      {/* Price + Key Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="border-border bg-card col-span-2 sm:col-span-1">
          <CardContent className="p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-teal-400">${p.list_price?.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">List Price</p>
            {p.price_per_sqft && <p className="text-[10px] text-muted-foreground mt-0.5">${p.price_per_sqft}/sqft</p>}
          </CardContent>
        </Card>
        <StatBox label="Beds" value={p.beds} icon={<Bed size={14} />} />
        <StatBox label="Baths" value={p.baths} icon={<Bath size={14} />} />
        <StatBox label="Sqft" value={p.sqft?.toLocaleString()} icon={<Maximize2 size={14} />} />
        <StatBox label="Year Built" value={p.year_built || 'N/A'} icon={<Calendar size={14} />} />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Description + Features */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-foreground text-base">Description</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-foreground leading-relaxed">{p.description || 'No description available.'}</p>
            {p.features?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Features</p>
                <div className="flex flex-wrap gap-1.5">
                  {p.features.map((f, i) => (
                    <Badge key={i} className="text-[10px] bg-muted text-foreground border-border">{f}</Badge>
                  ))}
                </div>
              </div>
            )}
            {/* Listing info */}
            <div className="border-t border-border pt-3 space-y-1.5">
              {p.listing_agent_name && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Listing Agent</span>
                  <span className="text-foreground">{p.listing_agent_name}</span>
                </div>
              )}
              {p.listing_office && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Office</span>
                  <span className="text-foreground">{p.listing_office}</span>
                </div>
              )}
              {p.days_on_market > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Days on Market</span>
                  <span className="text-foreground">{p.days_on_market}</span>
                </div>
              )}
              {p.hoa_fee && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">HOA Fee</span>
                  <span className="text-foreground">${p.hoa_fee}/mo</span>
                </div>
              )}
              {p.lot_sqft && (
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Lot Size</span>
                  <span className="text-foreground">{p.lot_sqft?.toLocaleString()} sqft</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Price History + Events */}
        <div className="space-y-5">
          {/* Price History */}
          <Card className="border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <DollarSign size={14} className="text-teal-400" /> Price History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {priceHistory.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No price history yet</p>
              ) : (
                <div className="space-y-2">
                  {priceHistory.map((ph, i) => (
                    <div key={ph.id || i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                      <div className="flex items-center gap-2">
                        {ph.event === 'price_decrease' ? <TrendingDown size={14} className="text-emerald-400" />
                          : ph.event === 'price_increase' ? <TrendingUp size={14} className="text-red-400" />
                          : <DollarSign size={14} className="text-muted-foreground" />}
                        <div>
                          <p className="text-sm text-foreground font-medium">${ph.price?.toLocaleString()}</p>
                          {ph.previous_price && (
                            <p className="text-[10px] text-muted-foreground">
                              from ${ph.previous_price?.toLocaleString()} ({ph.change_pct > 0 ? '+' : ''}{ph.change_pct}%)
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-[10px] text-muted-foreground">{formatDate(ph.changed_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Events */}
          <Card className="border-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <Clock size={14} className="text-blue-400" /> Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No activity yet</p>
              ) : (
                <div className="space-y-2">
                  {events.map((ev, i) => (
                    <div key={ev.id || i} className="flex items-start gap-2 py-1.5 border-b border-border last:border-0">
                      <div className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        ev.event_type === 'new_listing' ? 'bg-emerald-500/10' :
                        ev.event_type === 'price_change' ? 'bg-blue-500/10' :
                        'bg-accent/50'
                      }`}>
                        {ev.event_type === 'new_listing' ? <Home size={10} className="text-emerald-400" /> :
                         ev.event_type === 'price_change' ? <DollarSign size={10} className="text-blue-400" /> :
                         <ArrowUpDown size={10} className="text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-foreground">{formatEventType(ev.event_type)}</p>
                        {ev.details && (
                          <p className="text-[10px] text-muted-foreground truncate">{formatEventDetails(ev)}</p>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(ev.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Analysis */}
      <AnalysisCard analysis={analysis} onRun={runAnalysis} analyzing={analyzing} />

      {/* CMA Report */}
      <CMACard cma={cma} onGenerate={generateCma} generating={generatingCma} onShare={shareCma} />

      {/* Deal Score */}
      <DealScoreCard dealScore={dealScore} onScore={runScoring} scoring={scoring} />
    </div>
  );
}


function CMACard({ cma, onGenerate, generating, onShare }) {
  return (
    <Card className="border-border bg-card" data-testid="cma-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <BarChart3 size={16} className="text-blue-400" /> Comparative Market Analysis
          </CardTitle>
          <div className="flex gap-1.5">
            {cma && (
              <Button size="sm" variant="outline" onClick={onShare}
                className="border-border text-foreground hover:bg-muted text-[10px] h-7 gap-1"
                data-testid="share-cma-btn">
                <Send size={10} /> {cma.is_shared ? 'Link Copied' : 'Share'}
              </Button>
            )}
            <Button size="sm" disabled={generating} onClick={onGenerate}
              className="bg-blue-600 hover:bg-blue-500 text-white text-[10px] h-7 gap-1"
              data-testid="generate-cma-btn">
              {generating ? <Loader2 size={12} className="animate-spin" /> : <BarChart3 size={12} />}
              {generating ? 'Generating...' : cma ? 'Regenerate' : 'Generate CMA'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!cma ? (
          <div className="py-6 text-center">
            <BarChart3 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Generate a CMA to compare this property with similar listings in the area.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Key metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className="text-base font-bold text-foreground">${cma.avg_comp_price?.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">Avg Comp Price</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className="text-base font-bold text-foreground">${cma.avg_comp_ppsqft}/sqft</p>
                <p className="text-[10px] text-muted-foreground">Avg $/Sqft</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className={`text-base font-bold ${cma.price_diff_pct < 0 ? 'text-emerald-400' : cma.price_diff_pct > 5 ? 'text-red-400' : 'text-foreground'}`}>
                  {cma.price_diff_pct > 0 ? '+' : ''}{cma.price_diff_pct?.toFixed(1)}%
                </p>
                <p className="text-[10px] text-muted-foreground">vs Comps</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className="text-sm font-bold text-teal-400">${cma.suggested_range_low?.toLocaleString()}-${cma.suggested_range_high?.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">Suggested Range</p>
              </div>
            </div>

            {/* Comparables table */}
            <div>
              <p className="text-xs text-muted-foreground mb-2 font-medium">{cma.comparables?.length || 0} Comparable Properties</p>
              <div className="space-y-1.5">
                {(cma.comparables || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-muted rounded-lg text-xs">
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground truncate">{c.address_line}</p>
                      <p className="text-[10px] text-muted-foreground">{c.beds}bd/{c.baths}ba &middot; {c.sqft?.toLocaleString()} sqft &middot; {c.status}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-foreground font-medium">${c.list_price?.toLocaleString()}</p>
                      {c.price_diff_pct != null && (
                        <p className={`text-[10px] ${c.price_diff_pct < 0 ? 'text-emerald-400' : c.price_diff_pct > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                          {c.price_diff_pct > 0 ? '+' : ''}{c.price_diff_pct?.toFixed(1)}% &middot; ${c.price_per_sqft}/sqft
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Market summary */}
            <div className="bg-muted rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1 font-medium">Market Summary</p>
              <p className="text-xs text-foreground leading-relaxed">{cma.market_summary}</p>
            </div>

            {/* Pricing recommendation */}
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
              <p className="text-xs text-blue-400 mb-1 font-medium">Pricing Recommendation</p>
              <p className="text-xs text-foreground leading-relaxed">{cma.pricing_recommendation}</p>
            </div>

            {/* AI narrative */}
            {cma.ai_narrative && (
              <div className="bg-teal-500/5 border border-teal-500/20 rounded-lg p-3">
                <p className="text-xs text-teal-400 mb-1 font-medium flex items-center gap-1"><Sparkles size={10} /> AI Market Narrative</p>
                <p className="text-xs text-foreground leading-relaxed">{cma.ai_narrative}</p>
              </div>
            )}

            {/* Share info */}
            {cma.is_shared && (
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <ExternalLink size={10} />
                <span>Shareable link active — anyone with the link can view this CMA</span>
              </div>
            )}

            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <Badge className={`text-[10px] ${cma.analysis_type === 'ai_enhanced' ? 'bg-teal-500/10 text-teal-400 border-teal-500/20' : 'bg-accent/50 text-muted-foreground border-border'}`}>
                {cma.analysis_type === 'ai_enhanced' ? 'AI Enhanced' : 'Rules'}
              </Badge>
              <span>{formatDate(cma.created_at)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


const LEVEL_STYLES = {
  exceptional: { color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', label: 'Exceptional' },
  strong: { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Strong' },
  moderate: { color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', label: 'Moderate' },
  low: { color: 'text-muted-foreground', bg: 'bg-secondary border-border', label: 'Low' },
};

function DealScoreCard({ dealScore, onScore, scoring }) {
  const ds = dealScore;
  const level = ds ? LEVEL_STYLES[ds.opportunity_level] || LEVEL_STYLES.low : null;

  return (
    <Card className="border-border bg-card" data-testid="deal-score-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Trophy size={16} className="text-amber-400" /> Deal Score
          </CardTitle>
          <Button size="sm" disabled={scoring} onClick={onScore}
            className="bg-amber-600 hover:bg-amber-500 text-foreground text-[10px] h-7 gap-1"
            data-testid="run-deal-score-btn">
            {scoring ? <Loader2 size={12} className="animate-spin" /> : <Target size={12} />}
            {scoring ? 'Scoring...' : ds ? 'Rescore' : 'Score Deal'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!ds ? (
          <div className="py-6 text-center">
            <Trophy className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Score this property to evaluate its investment potential.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Score ring + level */}
            <div className="flex items-center gap-5">
              <div className="relative h-20 w-20 shrink-0">
                <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-foreground" />
                  <circle cx="18" cy="18" r="15.9" fill="none" strokeWidth="2.5"
                    strokeDasharray={`${ds.score}, 100`}
                    className={level.color}
                    strokeLinecap="round" stroke="currentColor" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-xl font-bold ${level.color}`}>{ds.score}</span>
                </div>
              </div>
              <div>
                <Badge className={`text-xs ${level.bg}`}>{level.label}</Badge>
                <p className="text-xs text-muted-foreground mt-1.5">{ds.score}/100 investment score</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Updated {formatDate(ds.updated_at)}</p>
              </div>
            </div>

            {/* Score Components */}
            {ds.score_components && (
              <div className="space-y-1.5" data-testid="score-components">
                {Object.entries(ds.score_components).filter(([, v]) => v > 0).map(([key, val]) => {
                  const label = key.replace(/_score$/, '').replace(/_/g, ' ');
                  const maxMap = { price_drop: 20, dom: 15, value_signal: 18, rental_signal: 12, appreciation_signal: 13, liquidity_signal: 10, match_demand: 12 };
                  const max = maxMap[key.replace(/_score$/, '')] || 20;
                  const pct = Math.min(100, (val / max) * 100);
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-[10px] mb-0.5">
                        <span className="text-muted-foreground capitalize">{label}</span>
                        <span className="text-foreground">{val}/{max}</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : 'bg-muted-foreground'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reasons */}
            {ds.reasons?.length > 0 && (
              <div data-testid="deal-score-reasons">
                <p className="text-xs text-muted-foreground font-medium mb-1.5">Key Factors</p>
                <ul className="space-y-1">
                  {ds.reasons.map((r, i) => (
                    <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                      <Target size={10} className="text-amber-400 mt-0.5 shrink-0" /> {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}



const SIGNAL_COLORS = {
  strong: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  moderate: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  weak: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  unknown: 'text-muted-foreground bg-secondary border-border',
};

const POSITION_COLORS = {
  below_market: 'text-emerald-400 bg-emerald-500/10',
  near_market: 'text-blue-400 bg-blue-500/10',
  above_market: 'text-red-400 bg-red-500/10',
  unknown: 'text-muted-foreground bg-secondary',
};

function AnalysisCard({ analysis, onRun, analyzing }) {
  const a = analysis;

  return (
    <Card className="border-border bg-card" data-testid="analysis-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Sparkles size={16} className="text-teal-400" /> AI Analysis
          </CardTitle>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" disabled={analyzing}
              onClick={() => onRun(false)}
              className="border-border text-foreground hover:bg-muted text-[10px] h-7"
              data-testid="run-rules-analysis-btn">
              {analyzing ? <Loader2 size={12} className="animate-spin mr-1" /> : <BarChart3 size={12} className="mr-1" />}
              Rules
            </Button>
            <Button size="sm" disabled={analyzing}
              onClick={() => onRun(true)}
              className="bg-teal-600 hover:bg-teal-500 text-foreground text-[10px] h-7"
              data-testid="run-ai-analysis-btn">
              {analyzing ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />}
              AI Analyze
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!a ? (
          <div className="py-6 text-center">
            <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No analysis yet. Click "AI Analyze" to generate insights.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div>
              <p className="text-sm text-foreground leading-relaxed">{a.summary}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={`text-[10px] ${a.analysis_type === 'ai_enhanced' ? 'bg-teal-500/10 text-teal-400 border-teal-500/20' : 'bg-accent/50 text-muted-foreground border-border'}`}>
                  {a.analysis_type === 'ai_enhanced' ? 'AI Enhanced' : 'Rules'}
                </Badge>
                {a.ai_confidence && (
                  <span className="text-[10px] text-muted-foreground">{Math.round(a.ai_confidence * 100)}% confidence</span>
                )}
              </div>
            </div>

            {/* Signal Badges */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="signal-badges">
              <SignalBadge label="Price" value={a.price_position?.replace('_', ' ')} colors={POSITION_COLORS[a.price_position] || POSITION_COLORS.unknown} />
              <SignalBadge label="Value" value={a.value_signal} colors={SIGNAL_COLORS[a.value_signal] || SIGNAL_COLORS.unknown} />
              <SignalBadge label="Rental" value={a.rental_signal} colors={SIGNAL_COLORS[a.rental_signal] || SIGNAL_COLORS.unknown} />
              <SignalBadge label="Appreciation" value={a.appreciation_signal} colors={SIGNAL_COLORS[a.appreciation_signal] || SIGNAL_COLORS.unknown} />
              <SignalBadge label="Liquidity" value={a.liquidity_signal} colors={SIGNAL_COLORS[a.liquidity_signal] || SIGNAL_COLORS.unknown} />
            </div>

            {/* Strengths + Risks */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {a.strengths?.length > 0 && (
                <div>
                  <p className="text-xs text-emerald-400 font-medium mb-1.5 flex items-center gap-1"><Shield size={12} /> Strengths</p>
                  <ul className="space-y-1">
                    {a.strengths.map((s, i) => (
                      <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                        <span className="text-emerald-500 mt-0.5 shrink-0">+</span> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {a.risks?.length > 0 && (
                <div>
                  <p className="text-xs text-amber-400 font-medium mb-1.5 flex items-center gap-1"><AlertTriangle size={12} /> Risks</p>
                  <ul className="space-y-1">
                    {a.risks.map((r, i) => (
                      <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                        <span className="text-amber-500 mt-0.5 shrink-0">!</span> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Opportunity Tags */}
            {a.opportunity_tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {a.opportunity_tags.map((t, i) => (
                  <Badge key={i} className="text-[10px] bg-teal-500/10 text-teal-400 border-teal-500/20">
                    <Zap size={8} className="mr-0.5" /> {t.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            )}

            {/* Estimated rent */}
            {a.estimated_rent && (
              <p className="text-[10px] text-muted-foreground">Estimated rent: ${a.estimated_rent?.toLocaleString()}/mo</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SignalBadge({ label, value, colors }) {
  return (
    <div className={`rounded-lg p-2 text-center ${colors}`}>
      <p className="text-[10px] opacity-70">{label}</p>
      <p className="text-xs font-semibold capitalize">{value || 'unknown'}</p>
    </div>
  );
}


function MiniStat({ label, value, icon, color, bg }) {
  return (
    <Card className="border-border bg-card">
      <CardContent className="flex items-center gap-3 p-3">
        <div className={`h-8 w-8 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
          <span className={color}>{icon}</span>
        </div>
        <div>
          <p className="text-base font-bold text-foreground">{value}</p>
          <p className="text-[10px] text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StatBox({ label, value, icon }) {
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-3 text-center">
        <span className="text-muted-foreground flex justify-center mb-1">{icon}</span>
        <p className="text-lg font-bold text-foreground">{value}</p>
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}


function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatEventType(type) {
  const labels = {
    new_listing: 'New Listing',
    price_change: 'Price Changed',
    status_change: 'Status Changed',
    delisted: 'Delisted',
    relisted: 'Relisted',
  };
  return labels[type] || type;
}

function formatEventDetails(ev) {
  const d = ev.details || {};
  if (ev.event_type === 'new_listing') {
    return `${d.address || ''} - $${(d.price || 0).toLocaleString()} - ${d.beds || 0}bd in ${d.city || ''}`;
  }
  if (ev.event_type === 'price_change') {
    return `$${(d.old_price || 0).toLocaleString()} -> $${(d.new_price || 0).toLocaleString()} (${d.change_pct > 0 ? '+' : ''}${d.change_pct}%)`;
  }
  if (ev.event_type === 'status_change') {
    return `${d.old_status} -> ${d.new_status}`;
  }
  return JSON.stringify(d);
}
